import { useState, useEffect } from 'react';
import { usersGraphqlService, UserGroupDashboard } from '@/services/api/users/usersGraphqlService';
import { useAuth } from '@/contexts/AuthContext';
import { apiClient } from '@/services/core/apiClient';
import { useErrorHandler } from '@/hooks/useErrorHandler';

interface UpdateUserGroupPayload {
  userGroupName: string;
  userIds: number[];
}

interface UseEditUserGroupResult {
  userGroup: UserGroupDashboard | null;
  assignedUserIds: number[];
  loading: boolean;
  submitting: boolean;
  updateUserGroup: (data: UpdateUserGroupPayload) => Promise<boolean>;
}

export const useEditUserGroup = (userGroupId: string): UseEditUserGroupResult => {
  const { token, hasuraToken, user: authUser } = useAuth();
  const { handleError, handleSuccess } = useErrorHandler();
  const [userGroup, setUserGroup] = useState<UserGroupDashboard | null>(null);
  const [assignedUserIds, setAssignedUserIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchUserGroup = async () => {
      if (!hasuraToken || !userGroupId) {
        setLoading(false);
        return;
      }

      try {
        const response = await usersGraphqlService.getUserGroupById(
          hasuraToken,
          parseInt(userGroupId)
        );

        if (response.success && response.data?.pep_vw_user_group_dashboard?.[0]) {
          const groupData = response.data.pep_vw_user_group_dashboard[0];
          setUserGroup(groupData);
          setAssignedUserIds(groupData.user_ids || []);
        } else {
          handleError('User group not found');
        }
      } catch (err) {
        handleError(err, 'Failed to fetch user group details');
      } finally {
        setLoading(false);
      }
    };

    fetchUserGroup();
  }, [hasuraToken, userGroupId, handleError]);

  const updateUserGroup = async (formData: UpdateUserGroupPayload): Promise<boolean> => {
    if (!authUser?.id || !userGroupId) {
      handleError('Authentication required');
      return false;
    }

    setSubmitting(true);

    try {
      const requestBody = {
        userGroupName: formData.userGroupName,
        userIds: formData.userIds
      };

      const endpoint = `/api/ngpep/${authUser.id}/usergroup/${userGroupId}`;
      const response = await apiClient.put(endpoint, requestBody, token);

      if (response.success) {
        handleSuccess('User group updated successfully');
        return true;
      } else {
        handleError(response.error || 'Failed to update user group');
        return false;
      }
    } catch (err) {
      handleError(err, 'Failed to update user group');
      return false;
    } finally {
      setSubmitting(false);
    }
  };

  return {
    userGroup,
    assignedUserIds,
    loading,
    submitting,
    updateUserGroup,
  };
};
